import { defineComponent as _defineComponent } from 'vue'
import {
  computed, reactive, ref, watch
} from 'vue';
import BCXInfoSection from '@/components/molecules/BCXInfoSection.vue';
import ProjectSelector from '@/views/project-forum/components/sidebar-actions/ProjectSelector.vue';
import { useProjectGuests } from '@/views/project-guests/composables/useProjectGuests';
import { ExternalProjectListEntry } from '@/__generated__/types';
import BCXValidatedInput from '@/components/molecules/BCXValidatedInput.vue';
import useBCValidators from '@/mixins/useBCValidators';
import useVuelidate from '@vuelidate/core';
import BCXTextArea from '@/components/molecules/forms/BCXTextArea.vue';
import ProjectMinimalListItem from '@/views/project-forum/components/ProjectMinimalListItem.vue';
import ConfirmButtons from '@/components/molecules/ConfirmButtons.vue';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import BCXCheckbox from '@/components/molecules/forms/BCXCheckbox.vue';
import MinusIcon from '@/components/svg/minus-circle.svg?inline.svg';
import CompanyService from '@/services/CompanyService';
import useUser from '@/mixins/useUser';
import { AxiosError } from 'axios';
import getAxiosResponseErrorCode from '@/utils/getAxiosResponseErrorCode';
import useI18n from '@/mixins/useI18n';
import TranslatedBackendErrors from '@/utils/TranslatedBackendErrors';
import BackendErrors from '@/components/login-register/BackendErrors.vue';
import { TranslatedErrorCode } from '@/models/BackendError';


export default /*#__PURE__*/_defineComponent({
  __name: 'AddGuest',
  emits: ['invitation-sent'],
  setup(__props, { emit }) {

const { companyId } = useUser('self');
const { projectList, projectName } = useProjectGuests();
const { emailValidation, required } = useBCValidators();
const { t } = useI18n();

const isMessageEnlarged = ref(false);
const projectsToAdd = ref<ExternalProjectListEntry[]>([]);
const hasInvited = ref(false);
const isChecked = ref(false);
const isResendButtonDisabled = ref(true);
const errorMessage = ref('');
const errorCodes = ref<(TranslatedErrorCode | string)[]>([]);



const initialFields = {
  firstname: '',
  lastname: '',
  email: '',
  message: ''
};
const fields = reactive({ ...initialFields });
const rules = {
  firstname: { required },
  lastname: { required },
  email: emailValidation
};

const v = useVuelidate(rules, fields);

const addProject = (project: ExternalProjectListEntry) => {
  if (projectsToAdd.value.find(({ id }) => id === project?.id)) return;
  projectsToAdd.value.push(project);
  projectName.value = '';
};

const onSelectProject = (project: ExternalProjectListEntry) => {
  if (!project) return;
  addProject(project);
};

const removeProject = (id: string) => {
  const index = projectsToAdd.value.findIndex((project) => project.id === id);
  if (index >= 0) projectsToAdd.value.splice(index, 1);
};

const hasAnyValue = computed(() => v.value?.lastname?.$model
  || v.value?.firstname?.$model
  || v.value?.lastname?.$model
  || v.value?.email?.$model
  || fields.message
  || projectsToAdd.value.length || isChecked.value);

const canSend = computed(() => v.value?.firstname?.$dirty
  && v.value?.lastname?.$dirty
  && v.value?.email?.$dirty
  && !v.value.$errors?.length
  && projectsToAdd.value.length
  && isChecked.value);

const resetFields = (preserveMessageAndProjects = false) => {
  if (!preserveMessageAndProjects) {
    Object.assign(fields, initialFields);
    projectsToAdd.value = [];
  }
  v.value.firstname.$model = '';
  v.value.email.$model = '';
  v.value.lastname.$model = '';
  isChecked.value = false;
  v.value.$reset();
};

const remainingProjectsList = computed(
  () => projectList.value.filter(
    (project) => !projectsToAdd.value.some(
      (addedProject) => addedProject.id === project.id
    )
  )
);

watch(projectsToAdd, (projectsToAdd) => {
  if (!projectsToAdd.length) {
    isChecked.value = false;
  }
}, { deep: true });

const inviteGuests = async () => {
  try {
    const axiosResponse = await CompanyService.inviteCompanyGuest(companyId.value, {
      firstName: v.value?.firstname?.$model ?? '',
      lastName: v.value?.lastname?.$model ?? '',
      email: v.value?.email?.$model ?? '',
      message: fields.message,
      projectList: projectsToAdd.value.map((project) => project.id)
    });

    if (axiosResponse.status < 400) {
      errorMessage.value = '';
      errorCodes.value = [];
      emit('invitation-sent');
    }
  } catch (err) {
    errorCodes.value = [];

    const error = err as AxiosError;
    if (error.response) {
      const backendErrors = new TranslatedBackendErrors(error.response);
      if (backendErrors.hasErrors()) {
        errorCodes.value = backendErrors.allErrors;
        errorMessage.value = ''; // Clear any existing error message
      } else {
        errorMessage.value = t('project-guests.errors.invite-guest-error', {
          code: getAxiosResponseErrorCode(error) ?? 'UNKNOWN_ERROR',
        });
      }
    } else {
      // Handle network errors or other errors without a response
      errorMessage.value = t('project-guests.errors.invite-guest-error', {
        code: error.code ?? 'NETWORK_ERROR',
      });
    }
    isResendButtonDisabled.value = true;
    setTimeout(() => {
      isResendButtonDisabled.value = false;
    }, 5000);
  }
  hasInvited.value = true;
};

const newInvite = () => {
  resetFields(true);
  hasInvited.value = false;
};


return { __sfc: true,companyId, projectList, projectName, emailValidation, required, t, isMessageEnlarged, projectsToAdd, hasInvited, isChecked, isResendButtonDisabled, errorMessage, errorCodes, emit, initialFields, fields, rules, v, addProject, onSelectProject, removeProject, hasAnyValue, canSend, resetFields, remainingProjectsList, inviteGuests, newInvite, BCXInfoSection, ProjectSelector, BCXValidatedInput, BCXTextArea, ProjectMinimalListItem, ConfirmButtons, BCXMarkdown, BCXCheckbox, MinusIcon, BackendErrors }
}

})